import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { FaExternalLinkAlt, FaGithub, FaYoutube, FaImage, FaCode, FaStar } from "react-icons/fa";
import { HiOutlineStatusOnline } from "react-icons/hi";
import { MdOutlineCategory } from "react-icons/md";
import projectsData from "../../data/projects";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState({});

  useEffect(() => {
    const sorted = [...projectsData].sort((a, b) => a.priority - b.priority);
    setProjects(sorted);
    setLoading(false);
  }, []);

  const handleImageError = (projectId) => {
    setFailedImages(prev => ({ ...prev, [projectId]: true }));
  };

  return (
    <section
      id="projects"
      className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50
        dark:from-[#0a0a18] dark:to-[#0f0f1a] transition-colors duration-300
        relative overflow-hidden"
    >
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-[#f29f67]/5 dark:bg-[#f29f67]/5 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-[#3b8ff3]/5 dark:bg-[#3b8ff3]/5 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section - Matching Certificates */}
        <div className="text-center mb-14">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#f29f67] to-[#3b8ff3] 
              flex items-center justify-center shadow-lg animate-float">
              <FaCode className="text-white text-3xl" />
            </div>
          </div>
          <h2
            className="text-3xl font-extrabold uppercase tracking-widest
              text-gray-800 dark:text-white mb-3"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Featured Projects
          </h2>
          <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-[#f29f67] to-[#3b8ff3] mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xl mx-auto">
            Some of my top recent projects, developed with modern technologies.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <ClipLoader color="#f29f67" size={48} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project._id}
                className="group relative rounded-2xl overflow-hidden
                  bg-white/90 dark:bg-white/5
                  backdrop-blur-sm
                  border border-gray-200 dark:border-white/10
                  hover:border-[#f29f67]/40 dark:hover:border-[#f29f67]/40
                  hover:shadow-2xl hover:shadow-[#f29f67]/10
                  hover:-translate-y-2
                  transition-all duration-300
                  flex flex-col"
              >
                {/* Category Badge - Matching Certificate Style */}
                <div className="absolute top-3 left-3 z-10
                  px-3 py-1.5 rounded-lg text-xs font-bold
                  bg-gradient-to-r from-[#f29f67] to-[#f29f67]/80
                  text-white shadow-md
                  flex items-center gap-1.5">
                  <MdOutlineCategory size={10} />
                  {project.category}
                </div>

                {/* Featured Badge (if priority is high) */}
                {project.priority === 1 && (
                  <div className="absolute top-3 right-3 z-10
                    px-2 py-1 rounded-lg text-[10px] font-semibold
                    bg-gradient-to-r from-yellow-500 to-orange-500
                    text-white shadow-md
                    flex items-center gap-1">
                    <FaStar size={8} />
                    Featured
                  </div>
                )}

                {/* Project Image Section with Fallback */}
                <div className="relative overflow-hidden h-52 bg-gradient-to-br from-gray-100 to-gray-200 
                  dark:from-gray-800 dark:to-gray-900">
                  {!failedImages[project._id] ? (
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-full h-full object-cover
                        group-hover:scale-110 transition-transform duration-700"
                      onError={() => handleImageError(project._id)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center 
                      text-gray-400 dark:text-gray-500">
                      <FaImage size={64} className="mb-3 opacity-30" />
                      <span className="text-sm font-medium">Project Preview</span>
                      <span className="text-xs opacity-60">Image unavailable</span>
                    </div>
                  )}
                  
                  {/* Overlay Gradient on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Hover Overlay Text */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 
                    group-hover:opacity-100 transition-all duration-300">
                    <span className="px-4 py-2 bg-white/90 dark:bg-black/80 rounded-lg text-xs font-semibold
                      text-gray-800 dark:text-white transform translate-y-4 group-hover:translate-y-0 transition-transform">
                      View Project Details
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3
                    className="text-sm font-bold text-gray-800 dark:text-white text-center mb-3 
                      leading-snug line-clamp-2 min-h-[2.5rem]"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {project.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-xs text-center mb-4 line-clamp-3 min-h-[3rem]">
                    {project.description || "A modern web application built with cutting-edge technologies delivering exceptional user experience."}
                  </p>

                  {/* Tech Stack Tags */}
                  {project.techStack && project.techStack.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                      {project.techStack.slice(0, 3).map((tech, idx) => (
                        <span key={idx} 
                          className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 
                            rounded-full text-[10px] font-medium 
                            text-gray-600 dark:text-gray-400
                            hover:bg-[#f29f67]/20 dark:hover:bg-[#f29f67]/20
                            transition-colors duration-200">
                          {tech}
                        </span>
                      ))}
                      {project.techStack.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 
                          rounded-full text-[10px] font-medium 
                          text-gray-600 dark:text-gray-400">
                          +{project.techStack.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Project Stats (Optional) */}
                  {project.stats && (
                    <div className="flex justify-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-400">
                      {project.stats.stars && (
                        <div className="flex items-center gap-1">
                          <FaStar size={10} className="text-yellow-500" />
                          <span>{project.stats.stars}</span>
                        </div>
                      )}
                      {project.stats.forks && (
                        <div className="flex items-center gap-1">
                          <FaCode size={10} />
                          <span>{project.stats.forks}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 justify-center mt-auto">
                    {project.deployedLink && (
                      <a
                        href={project.deployedLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                          bg-gradient-to-r from-[#3b8ff3] to-[#2563eb]
                          text-white
                          hover:shadow-lg hover:shadow-[#3b8ff3]/30 
                          hover:-translate-y-0.5
                          transition-all duration-200
                          relative overflow-hidden"
                      >
                        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-white/0 to-white/20 
                          transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500" />
                        <HiOutlineStatusOnline size={12} className="relative z-10" />
                        <span className="relative z-10">Live Demo</span>
                      </a>
                    )}
                    {project.githubLink && (
                      <a
                        href={project.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                          bg-gray-800 text-white hover:bg-gray-700
                          transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md
                          relative overflow-hidden"
                      >
                        <FaGithub size={11} />
                        <span>Source Code</span>
                      </a>
                    )}
                    {project.youtubeLink && (
                      <a
                        href={project.youtubeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                          bg-gradient-to-r from-red-600 to-red-500 text-white 
                          hover:from-red-700 hover:to-red-600
                          transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <FaYoutube size={11} />
                        <span>Watch Demo</span>
                      </a>
                    )}
                  </div>
                </div>

                {/* Decorative Corner */}
                <div className="absolute bottom-0 left-0 w-16 h-16 
                  bg-gradient-to-tr from-[#f29f67]/5 to-transparent 
                  rounded-tr-2xl pointer-events-none" />
              </div>
            ))}
          </div>
        )}

        {/* Stats Section */}
        {!loading && projects.length > 0 && (
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              🚀 {projects.length} innovative project{projects.length !== 1 ? 's' : ''} built and deployed
            </p>
          </div>
        )}
      </div>

      {/* Add custom animations to your CSS */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Projects;